import { CommutingTripService, CommutingTripSummary } from '@geovelo-frontends/commons';
import { Close } from '@mui/icons-material';
import { Box, CircularProgress, DialogProps, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dialog, RemoveIcon } from '../../components';

function RemoveJourneyDialog({
  commutingTrip,
  ...props
}: Omit<DialogProps, 'onClose'> & {
  commutingTrip: CommutingTripSummary;
  onClose: (removed?: boolean) => void;
}): React.JSX.Element {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  async function handleRemove() {
    setLoading(true);

    try {
      await CommutingTripService.removeCommutingTrip(commutingTrip.id);

      props.onClose(true);
      enqueueSnackbar(t('companies.pages.admin.company.data.remove_journey_dialog.success'));
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('companies.pages.admin.company.data.remove_journey_dialog.server_error'), {
        variant: 'error',
      });
    }

    setLoading(false);
  }

  return (
    <Dialog fullWidth loading={loading} maxWidth="sm" {...props}>
      <Box display="flex" flexDirection="column" gap={3}>
        <Typography fontSize="1.1rem" fontWeight={600}>
          {t('companies.pages.admin.company.data.remove_journey_dialog.title', {
            title: commutingTrip.title,
          })}
        </Typography>
        <Typography variant="body2">
          {t('companies.pages.admin.company.data.remove_journey_dialog.description')}
        </Typography>
        <Box alignItems="center" display="flex" gap={3} justifyContent="flex-end">
          <Button
            color="primary"
            disabled={loading}
            onClick={() => props.onClose()}
            startIcon={<Close />}
            variant="outlined"
          >
            {t('companies.pages.admin.company.data.remove_journey_dialog.actions.cancel')}
          </Button>
          <Button
            color="error"
            disabled={loading}
            onClick={handleRemove}
            startIcon={
              loading ? (
                <CircularProgress color="inherit" size={16} thickness={4} />
              ) : (
                <RemoveIcon />
              )
            }
            variant="outlined"
          >
            {t('companies.pages.admin.company.data.remove_journey_dialog.actions.confirm')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default RemoveJourneyDialog;
